import {
  computed,
  inject,
  Injectable,
  LOCALE_ID,
  signal
} from '@angular/core';
import { Observable, tap } from 'rxjs';

import { MarkerService } from '@app/modules/users/data-collector/marker.service';
import { getBrowserLanguage, indicateLoading, syncWithLocalStorage } from '@core/helpers';
import { environment } from '@env';

import {
  Invoice,
  PaymentApiResponse,
  PaymentMethod,
  PaymentSection,
  PaymentTabs,
  Region,
  Tab
} from './payment.entity';
import { PaymentService } from './payment.service';

// Google Analytics
declare const google_tag_manager: any;
declare const ym: any;

type GoogleAnalyticsData = {
  cid: string;
  sid: number | null;
};

@Injectable({
  providedIn: 'root'
})
export class PaymentState {
  private readonly localeId = inject(LOCALE_ID);
  private readonly paymentService = inject(PaymentService);
  private readonly markerService = inject(MarkerService);

  public readonly googleAnalyticsData = signal<GoogleAnalyticsData>({
    cid: this.markerService.marker,
    sid: null
  });

  public readonly currentRegionId = signal<number | null>(null);
  public readonly currentTabId = signal<number | null>(null);
  public readonly currentMethodId = signal<number | null>(null);
  public readonly regions = signal<Region[]>([]);
  public readonly giftCodesMethods = signal<PaymentMethod[]>([]);
  public readonly invoice = signal<Invoice | null>(null);
  public readonly isLoadingRegions = signal<boolean>(true);
  public readonly regionTabs = signal<Tab[]>([]);
  public readonly skinMethods = signal<PaymentMethod[]>([]);
  public readonly isLoadingTabs = signal<boolean>(true);
  public readonly currentTabType = signal<PaymentTabs|string>(PaymentTabs.DEFAULT);
  public readonly currentTab = signal<PaymentTabs|null>(null);
  public readonly methodIndexInTab = signal<number>(0);
  public readonly currentSection = signal<PaymentSection | null>(PaymentSection.DEPOSIT);

  public readonly currentRegion = computed(() => {
    const regions = this.regions();
    const currentRegionId = this.currentRegionId();
    const currentRegion = regions.find((region) => region.id === currentRegionId);

    if (currentRegion) {
      return currentRegion;
    }

    if (regions.length > 0 && !currentRegion) {
      return regions[0];
    }

    return undefined;
  });

  public readonly currentMethod = computed(() => {
    const currentMethodId = this.currentMethodId();
    const methods = this.currentTabMethods();

    if (currentMethodId === null && methods.length > 0) {
      this.setCurrentMethodId(methods[0].id);
    }
    
    const method = methods.find((method) => method.id === currentMethodId);

    // If no method is found but we have methods, return the first one as a fallback
    if (!method && methods.length > 0) {
      return methods[0];
    }
    
    return method;
  });

  public readonly currentTabMethods = computed(() => {
    const tabs = this.regionTabs();
    const currentTabId = this.currentTabId();

    return tabs.find((tab) => tab.id === currentTabId)?.methods || [];
  });

  public readonly moneyMethods = computed<Tab[]>(() => {
    const methods = this.regionTabs();

    if (methods) {
      return methods.filter((item) => item.methods.length > 0);
    }

    return [];
  });

  public readonly cryptoMethods = computed(() => {
    const methods = this.regionTabs().find((method) => method.name === 'Crypto');

    if (methods) {
      return [methods];
    }

    return [];
  });

  public readonly skinsMethods = computed(() => {
    const methods = this.regionTabs().find((method) => method.name === 'Skins');

    if (methods) {
      return [methods];
    }

    return [];
  });

  private readonly localStorageEffect = syncWithLocalStorage('payment', {
    currentRegionId: this.currentRegionId,
    currentTab: this.currentTab,
    currentSection: this.currentSection
  });

  constructor() {
    this.setGoogleAnalyticsData();
  }

  setCurrentRegionId(currentRegionId: number): void {
    this.currentRegionId.set(currentRegionId);
  }

  setCurrentMethodId(currentMethodId: number, tabId?: number): void {
    this.currentMethodId.set(currentMethodId);

    if (tabId) {
      this.currentTabId.set(tabId);
    }
  }

  loadRegions(): Observable<any> {
    const browserLanguage = getBrowserLanguage(false) || this.localeId;

    return this.paymentService.getRegions(browserLanguage).pipe(
      indicateLoading(this.isLoadingRegions),
      tap((response) => {
        const { regions, detectedRegion } = response;

        this.regions.set(regions.filter((region) => !environment.paymentRegionsToHide.includes(region.id)));
        this.currentRegionId.set(this.currentRegionId() || detectedRegion.id);
      })
    );
  }

  loadRegionTabs(regionId?: number): Observable<PaymentApiResponse.RegionTabs> {
    regionId = regionId || this.currentRegionId()!;

    return this.paymentService.getRegionTabs(regionId).pipe(
      indicateLoading(this.isLoadingTabs),
      tap((response) => {
        this.regionTabs.set(response.tabs);
        this.skinMethods.set(response.skinsMethods);

        const currentTab = this.currentTab();
        const region = this.currentRegion();
        const isRuLanguage = this.localeId === 'ru';
        const isRuRegion = region?.slug === 'russia';

        // TODO убрать когда решим, проблему исходного стейта
        if (
          !currentTab ||
          (currentTab === PaymentTabs.GGPAY && (!isRuRegion || !isRuLanguage))
        ) {
          this.currentTab.set(PaymentTabs.DEFAULT);
        }
      })
    );
  }

  createInvoice(
    methodId: number,
    amountUsd: number = 1,
    wallet: string | null = null
  ): Observable<PaymentApiResponse.PaymentInvoice> {
    this.setGoogleAnalyticsData();

    const { cid, sid } = this.googleAnalyticsData();
    let ycid = null;

    ym(99552096, 'getClientID', (clientId: string) => {
      ycid = clientId;
    });

    return this.paymentService
      .createInvoice(methodId, amountUsd, wallet, cid, sid, ycid)
      .pipe(tap((response) => this.invoice.set(response.invoice)));
  }

  loadGiftCodes(): Observable<PaymentApiResponse.GiftCodes> {
    return this.paymentService.getGiftCodes().pipe(
      tap((response) => {
        this.giftCodesMethods.set(response.codes);
      })
    );
  }

  applyGiftCode(giftCode: string): Observable<PaymentApiResponse.GiftCodeApplied> {
    return this.paymentService.useGiftCode(giftCode);
  }

  private setGoogleAnalyticsData(): void {
    const { googleAnalyticsId } = environment;
    const gtagKey = `G-${googleAnalyticsId}`;

    if (typeof google_tag_manager === 'undefined' || !google_tag_manager[gtagKey]) {
      return;
    }

    const { cid, sid } = this.googleAnalyticsData();

    let clientId = google_tag_manager[gtagKey].dataLayer.get('gtagApiResult.client_id') || cid;
    let sessionId = google_tag_manager[gtagKey].dataLayer.get('gtagApiResult.session_id') || sid;

    if (sessionId && String(sessionId).length === 10) {
      sessionId = Number(sessionId);
    } else {
      sessionId = null;
    }

    if (!clientId || String(clientId).length > 32) {
      clientId = null;
    }

    this.googleAnalyticsData.set({
      cid: clientId,
      sid: sessionId
    });
  }
}
